import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { markOnboardingComplete } from "@/lib/onboardingStorage";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import DailyCheckinCard from "@/components/DailyCheckinCard";
import { toast } from "sonner";
import CoachCodePreview from "@/components/CoachCodePreview";
import { useCoachCodeLookup, normalizeCoachCode } from "@/hooks/useCoachCodeLookup";
import {
  UserPlus, Flame, UtensilsCrossed, Dumbbell, ChevronRight,
  ChevronLeft, CheckCircle, Sparkles, Rocket,
} from "lucide-react";

const STEPS = [
  { id: "coach", label: "Coach", icon: UserPlus },
  { id: "checkin", label: "Check-in", icon: Flame },
  { id: "plans", label: "Plans", icon: UtensilsCrossed },
];

export default function StudentOnboarding({ open, onComplete }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [coachCode, setCoachCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [linked, setLinked] = useState(!!user?.coach_id);
  const { preview: coachPreview, checking: coachChecking } = useCoachCodeLookup(coachCode);

  const progress = ((step + 1) / STEPS.length) * 100;

  const handleLinkCoach = async (e) => {
    e.preventDefault();
    if (!coachCode.trim()) return;
    setLinking(true);
    try {
      await api.post("/students/link-coach", { coach_code: coachCode.trim().toUpperCase() });
      toast.success("Coach associé !");
      setLinked(true);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code coach invalide");
    } finally {
      setLinking(false);
    }
  };

  const finish = () => {
    markOnboardingComplete(user?.user_id);
    onComplete?.();
  };

  const handleNext = () => {
    if (step === 0 && !linked && !user?.coach_id) {
      toast.info("Associez un coach ou passez cette étape pour continuer");
      return;
    }
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      finish();
    }
  };

  const handleSkipCoach = () => {
    setStep(1);
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="p-0 gap-0 flex flex-col overflow-hidden
          fixed inset-0 z-50 w-full max-w-none h-[100dvh] max-h-[100dvh]
          translate-x-0 translate-y-0 rounded-none border-0
          sm:inset-auto sm:left-[50%] sm:top-[50%] sm:max-w-lg sm:w-full
          sm:h-auto sm:max-h-[min(90dvh,720px)] sm:translate-x-[-50%] sm:translate-y-[-50%]
          sm:rounded-lg sm:border
          [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="student-onboarding"
      >
        {/* Header — fixe */}
        <div className="shrink-0 bg-gradient-to-br from-blue-600 to-indigo-700 px-6 pt-6 pb-5 text-white safe-area-top">
          <div className="flex items-center gap-2 mb-1">
            <Rocket className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-widest opacity-80">Bienvenue</span>
          </div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold">
            {user?.name ? `Bonjour ${user.name.split(" ")[0]} !` : "Bienvenue sur MyCoaching"}
          </h2>
          <p className="text-sm text-blue-100 mt-1">Configurons votre espace en 3 étapes rapides</p>
          <Progress value={progress} className="mt-4 h-1.5 bg-blue-500/40" />
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors whitespace-nowrap shrink-0 ${
                  i === step ? "bg-white/20 text-white" : i < step ? "text-blue-200" : "text-blue-300/60"
                }`}
              >
                {i < step ? <CheckCircle className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
                {s.label}
              </div>
            ))}
          </div>
        </div>

        {/* Corps — scrollable sur mobile */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-5">
          {step === 0 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <UserPlus className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Associez votre coach</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Entrez le code fourni par votre coach pour accéder à vos plans personnalisés.
                  </p>
                </div>
              </div>

              {(linked || user?.coach_id) ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-green-800">Coach associé !</p>
                    <p className="text-xs text-green-600">Vous êtes prêt à commencer votre parcours.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleLinkCoach} className="space-y-3">
                  <Input
                    value={coachCode}
                    onChange={(e) => setCoachCode(normalizeCoachCode(e.target.value))}
                    placeholder="COACH-XXXXXX"
                    className="h-11 uppercase font-mono tracking-wider"
                    data-testid="onboarding-coach-code"
                  />
                  <CoachCodePreview preview={coachPreview} checking={coachChecking} codeLength={coachCode.trim().length} />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      type="submit"
                      disabled={linking || !coachCode.trim()}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-full h-11"
                    >
                      {linking ? "..." : "Rejoindre mon coach"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleSkipCoach}
                      className="text-slate-500 rounded-full h-11"
                    >
                      Plus tard
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Flame className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Votre check-in quotidien</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    30 secondes par jour pour suivre votre humeur, énergie et maintenir votre série 🔥
                  </p>
                </div>
              </div>
              {(linked || user?.coach_id) ? (
                <DailyCheckinCard />
              ) : (
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 text-sm text-slate-500 text-center">
                  Associez d'abord un coach pour activer les check-ins quotidiens.
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Découvrez vos plans</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Votre coach vous assignera des plans nutrition et entraînement personnalisés.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => { finish(); navigate("/student/diet-plan"); }}
                  className="flex flex-col items-center gap-3 p-5 rounded-2xl border-2 border-green-100 bg-green-50 hover:bg-green-100 transition-colors"
                >
                  <UtensilsCrossed className="w-8 h-8 text-green-600" />
                  <span className="text-sm font-semibold text-green-800">Mon Plan Diet</span>
                </button>
                <button
                  type="button"
                  onClick={() => { finish(); navigate("/student/training-plan"); }}
                  className="flex flex-col items-center gap-3 p-5 rounded-2xl border-2 border-orange-100 bg-orange-50 hover:bg-orange-100 transition-colors"
                >
                  <Dumbbell className="w-8 h-8 text-orange-600" />
                  <span className="text-sm font-semibold text-orange-800">Mon Entraînement</span>
                </button>
              </div>

              <p className="text-xs text-slate-400 text-center">
                Vous pourrez aussi consulter votre coach depuis « Mon Coach » dans le menu.
              </p>
            </div>
          )}
        </div>

        {/* Footer — fixe */}
        <div className="shrink-0 px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 safe-area-bottom">
          <Button
            variant="ghost"
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="gap-1 rounded-full"
          >
            <ChevronLeft className="w-4 h-4" /> Retour
          </Button>
          <span className="text-xs text-slate-400">{step + 1} / {STEPS.length}</span>
          {step < STEPS.length - 1 ? (
            <Button onClick={handleNext} className="gap-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full">
              Suivant <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={finish} className="gap-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full">
              C'est parti ! <CheckCircle className="w-4 h-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}